/**
 * CurrencyIcon — the three internal currencies rendered as their signature marks:
 *
 * - ``mora``    — a golden coin,
 * - ``primogem``— a cyan crystal,
 * - ``glitter`` — an amethyst four-point sparkle (звёздный блеск).
 *
 * If a brand asset URL is configured for the currency (via {@link BrandingProvider},
 * fed from ``/api/me`` ``branding`` → object storage), it is rendered as an ``<img>``
 * drawn into a square ``size``×``size`` box with ``object-fit: contain`` so it is never
 * stretched or cropped. Otherwise the built-in CSS/SVG glyph is used — no brand PNGs
 * are bundled. Sizes are in px. Decorative by default (``aria-hidden``); pass a
 * ``title`` to make it an accessible image.
 */
import type { CSSProperties } from "react";

import { useBranding } from "../branding/BrandingContext";

export type CurrencyKind = "mora" | "primogem" | "glitter";

export interface CurrencyIconProps {
  readonly kind: CurrencyKind;
  /** Edge size in px. Defaults to 24. */
  readonly size?: number;
  readonly title?: string;
  readonly className?: string;
  readonly style?: CSSProperties;
}

export function CurrencyIcon({
  kind,
  size = 24,
  title,
  className,
  style,
}: CurrencyIconProps): JSX.Element {
  const branding = useBranding();
  const src = branding[kind];

  const common = {
    className,
    role: title ? ("img" as const) : undefined,
    "aria-hidden": title ? undefined : true,
    "aria-label": title,
  };

  // Preferred path: brand asset from object storage (never stretched/cropped).
  if (src) {
    return (
      <img
        src={src}
        width={size}
        height={size}
        alt={title ?? ""}
        role={title ? "img" : undefined}
        aria-hidden={title ? undefined : true}
        draggable={false}
        className={className}
        style={{
          width: size,
          height: size,
          objectFit: "contain",
          display: "inline-block",
          verticalAlign: "middle",
          flex: "none",
          ...style,
        }}
      />
    );
  }

  // Fallback glyphs (no bundled PNGs).
  if (kind === "mora") {
    return (
      <span
        {...common}
        style={{
          display: "inline-block",
          width: size,
          height: size,
          borderRadius: "50%",
          background: "var(--ms-mora-coin)",
          boxShadow:
            "0 2px 7px rgba(201,155,78,0.35), inset 0 1px 1px rgba(255,255,255,0.55)",
          flex: "none",
          verticalAlign: "middle",
          ...style,
        }}
      />
    );
  }

  if (kind === "primogem") {
    const inner = Math.round(size * 0.7);
    return (
      <span
        {...common}
        style={{
          display: "inline-flex",
          width: size,
          height: size,
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
          verticalAlign: "middle",
          ...style,
        }}
      >
        <span
          style={{
            width: inner,
            height: inner,
            borderRadius: Math.max(3, Math.round(size * 0.18)),
            transform: "rotate(45deg)",
            background: "var(--ms-primogem-grad)",
            boxShadow: "0 2px 7px rgba(95,178,196,0.35)",
          }}
        />
      </span>
    );
  }

  // glitter — amethyst four-point sparkle
  return (
    <svg
      {...common}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="var(--ms-glitter)"
      style={{ flex: "none", verticalAlign: "middle", ...style }}
    >
      {title ? <title>{title}</title> : null}
      <path d="M12 2.8c.55 4.7 1.7 5.85 6.4 6.4-4.7.55-5.85 1.7-6.4 6.4-.55-4.7-1.7-5.85-6.4-6.4 4.7-.55 5.85-1.7 6.4-6.4z" />
    </svg>
  );
}
